/*
  Task Cache System
  - Caches tasks in localStorage with timestamps
  - Provides instant loading for better UX
  - Automatic cache invalidation
  - Background refresh without blocking UI
*/

const CACHE_KEY = 'task_cache_data';
const CACHE_TIMESTAMP_KEY = 'task_cache_timestamp';
const CACHE_EXPIRY_MINUTES = 5; // 5 dakika cache geçerli

// Cache'i temizle
export const clearTaskCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Cache temizleme hatası:', error);
  }
};

// Cache'den görevleri al
export const getCachedTasks = () => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (!cachedData || !timestamp) {
      return null;
    }
    
    const cacheAge = Date.now() - parseInt(timestamp);
    const cacheExpiry = CACHE_EXPIRY_MINUTES * 60 * 1000;
    
    // Cache süresi dolmuş mu kontrol et
    if (cacheAge > cacheExpiry) {
      clearTaskCache();
      return null;
    }
    
    return JSON.parse(cachedData);
  } catch (error) {
    console.warn('Cache okuma hatası:', error);
    clearTaskCache();
    return null;
  }
};

// Görevleri cache'e kaydet
export const setCachedTasks = (tasks, additionalData = {}) => {
  try {
    const cacheData = {
      tasks,
      timestamp: Date.now(),
      ...additionalData
    };
    
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Cache kaydetme hatası:', error);
  }
};

// Cache geçerliliğini kontrol et
export const isCacheValid = () => {
  try {
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const cacheAge = Date.now() - parseInt(timestamp);
    const cacheExpiry = CACHE_EXPIRY_MINUTES * 60 * 1000;
    
    return cacheAge <= cacheExpiry;
  } catch (error) {
    return false;
  }
};

// Cache yaşını al (dakika cinsinden)
export const getCacheAge = () => {
  try {
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return null;
    
    const cacheAge = Date.now() - parseInt(timestamp);
    return Math.floor(cacheAge / (60 * 1000)); // dakika cinsinden
  } catch (error) {
    return null;
  }
};

// Belirli bir görevi cache'den güncelle
export const updateCachedTask = (updatedTask) => {
  try {
    const cachedData = getCachedTasks();
    if (!cachedData || !cachedData.tasks) return;
    
    const tasks = cachedData.tasks.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    );
    
    setCachedTasks(tasks, {
      totalCount: cachedData.totalCount,
      completedCount: cachedData.completedCount
    });
  } catch (error) {
    console.warn('Cache görev güncelleme hatası:', error);
  }
};

// Cache'den görev sil
export const removeCachedTask = (taskId) => {
  try {
    const cachedData = getCachedTasks();
    if (!cachedData || !cachedData.tasks) return;
    
    const tasks = cachedData.tasks.filter(task => task.id !== taskId);
    
    setCachedTasks(tasks, {
      totalCount: cachedData.totalCount - 1,
      completedCount: cachedData.completedCount - (cachedData.tasks.find(t => t.id === taskId)?.status === 'done' ? 1 : 0)
    });
  } catch (error) {
    console.warn('Cache görev silme hatası:', error);
  }
};

// Cache'den görev ekle
export const addCachedTask = (newTask) => {
  try {
    const cachedData = getCachedTasks();
    if (!cachedData || !cachedData.tasks) return;
    
    const tasks = [newTask, ...cachedData.tasks];
    
    setCachedTasks(tasks, {
      totalCount: cachedData.totalCount + 1,
      completedCount: cachedData.completedCount + (newTask.status === 'done' ? 1 : 0)
    });
  } catch (error) {
    console.warn('Cache görev ekleme hatası:', error);
  }
};

// Cache istatistiklerini al
export const getCacheStats = () => {
  try {
    const cachedData = getCachedTasks();
    if (!cachedData) return null;
    
    return {
      taskCount: cachedData.tasks?.length || 0,
      cacheAge: getCacheAge(),
      isValid: isCacheValid(),
      lastUpdated: cachedData.timestamp ? new Date(cachedData.timestamp).toLocaleString('tr-TR') : null
    };
  } catch (error) {
    return null;
  }
};
